/**
 * H.E.A.R.T — Healthcare Earned Advanced Rebate Tracking.  Route: /solutions/heart
 * (/solutions/ims-hart redirects here — old email and deck links still resolve.)
 *
 * Styled to match the Sterile Mate homepage: full-bleed photographic hero under a
 * Executive palette (see /app/design_guidelines.json): obsidian #030712 / #070D1E /
 * #0B132B surfaces, white Outfit display headlines, a single Precision Sapphire accent
 * (sky-400/500), hairline rules and tabular numerals. No amber, no gradient bars.
 * Deck source: HART Executive Deck 10.10.2026 (TechInquest).
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
  Sparkles, ShieldCheck, Cloud, Lock, Database, Boxes, Stethoscope, ClipboardCheck,
  Truck, Code2, Play, ArrowRight, Server, Smartphone, CheckCircle2, CircleDollarSign, Minus,
  Search, Calculator,
} from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import { HeartLogo } from "@/components/brand/HeartLogo";
import SEO from "@/components/SEO";
import {
  AlertWorkbench, Counter, FollowOneDollar, LeakPillars, NearMiss, TierSimulator,
} from "@/components/hart/HartCinematic";
import HartDemoForm from "@/components/hart/HartDemoForm";
import HartRoiCalculator from "@/components/hart/HartRoiCalculator";
import { SOFTWARE_APPS, APP_ORDER, PLATFORM, COMPARISON } from "@/data/softwareApps";

const HERO_FRAMES = [
  "https://images.unsplash.com/photo-1626315869436-d6781ba69d6e?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
  "https://images.unsplash.com/photo-1573167507387-6b4b98cb7c13?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
];

const ERPS = ["Workday", "Oracle", "Infor", "SAP", "Dynamics 365"];

const SECURITY = [
  { icon: Cloud, t: "Cloud SaaS on AWS", d: "Multi-tenant with redundancy. No servers to buy, nothing to rack." },
  { icon: Lock, t: "Encrypted in transit and at rest", d: "Secured data stores with immutable audit logs." },
  { icon: ShieldCheck, t: "SSO Entra ID + MFA", d: "Role-based access that mirrors how finance is already structured." },
  { icon: Smartphone, t: "Every device, same experience", d: "Desktop, laptop, iPad, iPhone — nothing to install." },
];

const CAPABILITIES = [
  { t: "Live tier monitoring", d: "Position recalculated on every purchase; near-misses caught while the period is still open." },
  { t: "Five alert types", d: "Near-miss, shortfall, variance, expiry, overdue — each with dollars, days and an owner." },
  { t: "Rebate & contract tracking", d: "Every agreement, tier table and deadline in one governed system. Not a spreadsheet." },
  { t: "Tasks & audit trail", d: "Alerts become assigned work with escalation paths and a complete history." },
  { t: "Analytics & dashboards", d: "Board-ready views that drill from the region down to the contract line." },
  { t: "Supplier dispute portal", d: "Disputes resolved where the supplier can answer, dollars assigned to the right department." },
];

const PRODUCTS = [
  { id: "hart", icon: CircleDollarSign, badge: "Today's focus", t: "H.E.A.R.T Rebate Management",
    d: "Track and manage every rebate dollar. Live tiers, five alerts, recovery with an owner." },
  { id: "ims", icon: Boxes, badge: "Expanded below", t: "IMS Bill-Only Implants",
    d: "Manage bill-only implant procedures. Contract compliance enforced; suppliers submit in the portal." },
  { id: "compliance", icon: Stethoscope, badge: "Governance", t: "Compliance Tracking",
    d: "Physician and vendor vaccination or compliance status — healthcare and government." },
  { id: "approval", icon: ClipboardCheck, badge: "Workflow", t: "Request Approval Workflow",
    d: "Approval tracking for requests, such as product approval ahead of surgery." },
  { id: "courier", icon: Truck, badge: "Logistics", t: "Courier Service Management",
    d: "Transport-department moves of product and equipment between locations." },
  { id: "custom", icon: Code2, badge: "Engineering", t: "Custom Solutions",
    d: "Bespoke software built to your requirements — business problems turned into working systems." },
];

/* Executive eyebrow — hairline slate chip, no amber (design_guidelines.json) */
const Pill = ({ children, icon: Icon = Sparkles }) => (
  <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-slate-900/70 border border-white/10 rounded-md mb-5">
    <Icon className="w-3.5 h-3.5 text-sky-400" />
    <span className="text-[11px] font-bold text-slate-300 uppercase tracking-[0.18em]">{children}</span>
  </div>
);

const PillLight = ({ children, icon: Icon = Sparkles }) => (
  <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-slate-100 border border-slate-900/10 rounded-md mb-5">
    <Icon className="w-3.5 h-3.5 text-[#0284C7]" />
    <span className="text-[11px] font-bold text-slate-600 uppercase tracking-[0.18em]">{children}</span>
  </div>
);

export default function IMSHARTProductPage() {
  const reduce = useReducedMotion();
  const [frame, setFrame] = useState(0);
  const [roi, setRoi] = useState(null);
  const [videoOn, setVideoOn] = useState(false);

  useEffect(() => {
    if (reduce) return;
    const t = setInterval(() => setFrame((f) => (f + 1) % HERO_FRAMES.length), 7000);
    return () => clearInterval(t);
  }, [reduce]);

  return (
    <div className="min-h-screen overflow-x-hidden bg-white" data-testid="ims-hart-page">
      <SEO
        canonical="/solutions/heart"
        title="H.E.A.R.T — Healthcare Earned Advanced Rebate Tracking | Sterile Mate × TechInquest"
        description="H.E.A.R.T tracks every rebate dollar for health systems: live tier monitoring, five alert types with dollars, days and an owner, ERP-fed, AWS cloud SaaS. No Epic connection required."
        keywords={["healthcare rebate management software", "supply chain rebate tracking", "rebate tier monitoring", "bill-only implant software", "hospital ERP rebate recovery"]}
      />
      <SiteHeader />

      {/* ========================= HERO — photo + navy wash ===================== */}
      <section className="relative min-h-[88vh] flex items-center overflow-hidden bg-[#030712]"
        data-testid="hart-hero">
        <div className="absolute inset-0">
          {HERO_FRAMES.map((src, i) => (
            <motion.img key={src} src={src} alt="" loading={i === 0 ? "eager" : "lazy"}
              initial={false}
              animate={{ opacity: frame === i ? 1 : 0, scale: frame === i ? 1.06 : 1 }}
              transition={{ opacity: { duration: 1.4, ease: "easeInOut" }, scale: { duration: 8, ease: "linear" } }}
              className="absolute inset-0 w-full h-full object-cover" />
          ))}
          <div className="absolute inset-0 bg-gradient-to-r from-[#030712] via-[#030712]/92 to-[#030712]/60" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#070D1E]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-[1.05fr_0.95fr] gap-12 items-center w-full">
          <div>
            <div className="mb-7" data-testid="heart-wordmark-hero">
              <HeartLogo size={64} tone="dark" idSuffix="hero" />
            </div>

            <div className="inline-flex items-center gap-3 rounded-md border border-slate-700/60 bg-slate-900/80
              px-4 py-2 backdrop-blur-md mb-7" data-testid="hart-cobrand">
              <span className="text-[11px] font-black uppercase tracking-[0.18em] text-white">Sterile Mate</span>
              <span className="text-[10px] text-slate-500">/ AskIsaac</span>
              <span className="w-px h-4 bg-white/15" />
              <Sparkles className="w-3 h-3 text-sky-400" />
              <span className="text-[11px] font-black uppercase tracking-[0.18em] text-sky-400">TechInquest</span>
            </div>

            <h1 className="font-display text-white font-bold leading-[1.02] tracking-tight
              text-[2.6rem] sm:text-[3.4rem] lg:text-[4.4rem]">
              You already earned the money.<br />
              <span className="text-sky-400">H.E.A.R.T makes sure it comes back.</span>
            </h1>

            <p className="mt-7 text-lg md:text-xl leading-[1.55] text-slate-200/90 font-sans max-w-2xl">
              <span className="text-white font-semibold">H.E.A.R.T — Healthcare Earned
              Advanced Rebate Tracking.</span> One system of record for every rebate dollar: tracked, alerted,
              recovered. Fed from the ERP you already own, watching every tier while the period
              is still open.
            </p>

            <div className="mt-9 flex flex-wrap gap-3">
              <a href="#demo"
                className="inline-flex items-center gap-2 rounded-lg bg-[#0056A8] hover:bg-[#003366]
                  px-7 py-4 text-sm font-bold uppercase tracking-wider text-white transition-colors
                  shadow-lg shadow-blue-900/40"
                data-testid="hero-demo-cta">
                Request a H.E.A.R.T demo <ArrowRight className="w-4 h-4" />
              </a>
              <a href="#walkthrough"
                className="inline-flex items-center gap-2 rounded-lg border border-white/25 px-7 py-4
                  text-sm font-bold uppercase tracking-wider text-white transition-colors
                  hover:bg-white/10"
                data-testid="hero-video-cta">
                <Play className="w-4 h-4" /> Watch the platform demo
              </a>
              <a href="#roi"
                className="inline-flex items-center gap-2 rounded-lg border border-sky-500/40
                  bg-sky-500/10 px-7 py-4 text-sm font-bold uppercase tracking-wider
                  text-sky-300 transition-colors hover:bg-sky-500/20"
                data-testid="hero-roi-cta">
                <Calculator className="w-4 h-4" /> Run the CFO ROI model
              </a>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-6 max-w-xl border-t border-white/10 pt-7">
              {[
                { v: 97, suffix: "%+", l: "of health systems are not proactive on rebates" },
                { v: 5, suffix: "", l: "alert types, each with money, a clock and a name" },
                { v: 25, suffix: "+ yrs", l: "inside hospital IT, 12 building this product line" },
              ].map((s) => (
                <div key={s.l}>
                  <Counter to={s.v} suffix={s.suffix} className="text-3xl md:text-4xl font-bold text-white tabular-nums tracking-tight" />
                  <p className="text-[11px] text-slate-400 leading-snug mt-2">{s.l}</p>
                </div>
              ))}
            </div>
          </div>

          <motion.div
            initial={reduce ? false : { opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}>
            <TierSimulator />
          </motion.div>
        </div>

        <div className="absolute inset-x-0 bottom-0 h-px bg-white/10" />
      </section>

      {/* ================== executive status strip ================== */}
      <a href="#demo"
        className="group block bg-[#070D1E] border-y border-slate-800 transition-colors hover:bg-[#0B132B]"
        data-testid="hart-callout-strip">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex flex-col sm:flex-row sm:items-center
          gap-x-5 gap-y-2">
          <span className="inline-flex items-center gap-2 rounded-md border border-sky-500/30 bg-sky-500/10
            px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-sky-300 shrink-0">
            <span className="w-1.5 h-1.5 rounded-full bg-sky-400" /> Now deploying
          </span>
          <p className="text-sm text-slate-300 leading-snug">
            Rebate recovery for health systems &mdash; live tiers, five alerts, every open dollar
            with an owner.
          </p>
          <span className="sm:ml-auto inline-flex items-center gap-2 text-sm font-bold text-white
            shrink-0 group-hover:text-sky-300 transition-colors">
            Book the walkthrough <ArrowRight className="w-4 h-4" />
          </span>
        </div>
      </a>

      {/* ============== CHOOSE YOUR APPLICATION — 3D box pathways ============== */}
      <section id="applications" className="bg-white border-b border-slate-200 py-20 md:py-28"
        data-testid="hart-applications">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-14">
            <PillLight icon={Boxes}>Two applications</PillLight>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-slate-900 tracking-tight mb-5">
              Enterprise healthcare applications.
            </h2>
            <p className="text-base text-slate-600 leading-relaxed mb-7">
              Two enterprise systems, deployed across the whole health system or stood up
              independently. Both read from the ERP you already run, both require no Epic
              integration, and neither asks you to change your GPO. Open either one for its full
              feature list, competencies and compatibility.
            </p>
            <div className="inline-flex items-center gap-4 rounded-xl border border-slate-200 bg-slate-50 pl-5 pr-6 py-3.5"
              data-testid="hart-builtby">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-0.5">
                  Software developer
                </p>
                <p className="text-sm font-bold text-slate-700">Built by</p>
              </div>
              <img src={PLATFORM.builtBy.logo} alt="Techinquest"
                data-testid="hart-techinquest-logo" className="h-7 w-auto" />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-7 lg:gap-10" data-testid="hart-app-boxes">
            {APP_ORDER.map((slug, i) => {
              const app = SOFTWARE_APPS[slug];
              return (
                <Link key={slug} to={`/solutions/heart/${slug}`}
                  data-testid={`hart-app-box-${slug}`}
                  className="group relative flex flex-col rounded-2xl border border-slate-200 bg-white
                    overflow-hidden hover:border-slate-300 hover:shadow-2xl hover:shadow-slate-900/10
                    transition-all duration-300">
                  <div className="relative bg-gradient-to-b from-slate-100 to-slate-50 px-8 pt-10 pb-8
                    flex items-center justify-center overflow-hidden">
                    <span className="absolute top-5 left-5 inline-flex items-center gap-2 px-2.5 py-1
                      rounded-md bg-white border border-slate-200 text-[10px] font-black uppercase
                      tracking-[0.18em] text-slate-600">
                      {app.badge}
                    </span>
                    <span className="absolute top-5 right-5 hidden sm:block text-[10px] font-black uppercase
                      tracking-[0.18em] text-slate-400">
                      {app.edition}
                    </span>
                    <img src={app.box} alt={app.boxAlt}
                      data-testid={`hart-box-img-${slug}`}
                      className="w-full max-w-[290px] drop-shadow-[0_28px_45px_rgba(15,23,42,0.28)]
                        transition-transform duration-500 group-hover:-translate-y-2 group-hover:scale-[1.03]" />
                  </div>

                  <div className="flex flex-col flex-1 p-8">
                    <p className="text-2xl font-black text-slate-900 tracking-tight">{app.code}</p>
                    <p className="text-[11px] font-black uppercase tracking-[0.18em] mt-1.5 mb-4"
                      style={{ color: app.accent }}>
                      {app.expansion}
                    </p>
                    <p className="text-sm text-slate-600 leading-relaxed mb-6">{app.tagline}</p>

                    <p className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400 mb-3">
                      Top features
                    </p>
                    <ul className="space-y-2.5 mb-7">
                      {app.topFeatures.slice(0, 4).map((f) => (
                        <li key={f.t} className="flex gap-2.5 text-sm text-slate-700">
                          <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" style={{ color: app.accent }} />
                          {f.t}
                        </li>
                      ))}
                      <li className="text-xs text-slate-400 pl-7">
                        + {app.topFeatures.length - 4} more inside
                      </li>
                    </ul>

                    <div className="mt-auto pt-6 border-t border-slate-200">
                      <span className="inline-flex items-center gap-2 text-sm font-black text-slate-900
                        group-hover:gap-3.5 transition-all"
                        style={{ color: app.accent }}>
                        Open {app.code} <ArrowRight className="w-4 h-4" />
                      </span>
                      <p className="text-[11px] text-slate-400 mt-2">
                        Features, competencies and compatibility
                      </p>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>

          <p className="text-xs text-slate-500 mt-8 max-w-3xl leading-relaxed">
            Licensing is quoted per health system after a scoping call, based on facilities and
            modules selected.
          </p>

          {/* side-by-side feature matrix */}
          <div className="mt-20 pt-16 border-t border-slate-200" data-testid="hart-compare">
            <div className="max-w-2xl mb-10">
              <PillLight icon={Boxes}>Compare</PillLight>
              <h3 className="text-2xl md:text-3xl lg:text-4xl font-black text-slate-900 tracking-tight mb-4">
                Both systems, one view.
              </h3>
              <p className="text-base text-slate-600 leading-relaxed">
                They share the same enterprise platform and the same operating discipline. What
                differs is the money each one protects.
              </p>
            </div>

            <div className="overflow-x-auto -mx-6 px-6 md:mx-0 md:px-0">
              <table className="w-full border-collapse" data-testid="hart-compare-table">
                <thead>
                  <tr className="bg-[#050a16]">
                    <th className="text-left py-4 px-3 md:px-5 text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">
                      Capability
                    </th>
                    {APP_ORDER.map((slug) => {
                      const a = SOFTWARE_APPS[slug];
                      return (
                        <th key={slug} className="py-4 px-2 md:px-5 text-center w-[68px] md:w-[180px]">
                          <span className="block text-xs md:text-sm font-black text-white">{a.code}</span>
                          <span className="hidden md:block text-[9px] font-bold uppercase tracking-[0.14em] mt-0.5"
                            style={{ color: a.accent }}>
                            {a.badge}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON.map((g) => (
                    <React.Fragment key={g.group}>
                      <tr className="bg-slate-100">
                        <td colSpan={3} className="py-2.5 px-3 md:px-5 text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">
                          {g.group}
                        </td>
                      </tr>
                      {g.rows.map((r) => (
                        <tr key={r.f} className="border-b border-slate-200 hover:bg-slate-50 transition-colors"
                          data-testid={`compare-row-${r.f.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                          <td className="py-3.5 px-3 md:px-5 text-[13px] md:text-sm text-slate-700">{r.f}</td>
                          {[r.a, r.b].map((has, idx) => (
                            <td key={idx} className="py-3.5 px-2 md:px-5 text-center">
                              {has ? (
                                <CheckCircle2 className="w-5 h-5 mx-auto"
                                  style={{ color: SOFTWARE_APPS[APP_ORDER[idx]].accent }} />
                              ) : (
                                <Minus className="w-4 h-4 mx-auto text-slate-300" />
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mt-8">
              {APP_ORDER.map((slug) => {
                const a = SOFTWARE_APPS[slug];
                return (
                  <Link key={slug} to={`/solutions/heart/${slug}`}
                    data-testid={`hart-compare-cta-${slug}`}
                    className="inline-flex items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white px-6 py-4 hover:border-slate-300 hover:shadow-lg transition-all">
                    <span className="text-sm font-black text-slate-900">
                      Open {a.code}
                    </span>
                    <ArrowRight className="w-4 h-4" style={{ color: a.accent }} />
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>


      {/* ============== PLATFORM WALKTHROUGH — click-to-play facade ============== */}
      <section id="walkthrough" className="bg-[#0B132B] border-b border-slate-800 py-14 md:py-20"
        data-testid="hart-walkthrough">
        <div className="max-w-5xl mx-auto px-6">
          <div className="max-w-2xl mb-8">
            <p className="text-[10px] font-black uppercase tracking-[0.24em] text-sky-400 mb-3">
              Platform walkthrough · 1 of 1
            </p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-[2.5rem] font-bold text-white tracking-tight leading-[1.1] mb-3">
              See IMS and H.E.A.R.T running together.
            </h2>
            <p className="text-base text-slate-400 leading-relaxed">
              The implant case, the contract validation, the tier position and the recovered
              dollar — one pass through both platforms, no slides.
            </p>
          </div>

          <div className="relative rounded-lg overflow-hidden border border-white/10 bg-black aspect-video
            shadow-[0_40px_90px_-40px_rgba(0,0,0,0.9)]">
            {videoOn ? (
              <iframe
                className="w-full h-full"
                src="https://www.youtube.com/embed/Ng1roAYbGJc?autoplay=1&rel=0"
                title="IMS H.E.A.R.T Demo Video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                data-testid="ims-demo-video"
              />
            ) : (
              <button
                type="button"
                onClick={() => setVideoOn(true)}
                className="group absolute inset-0 w-full h-full"
                aria-label="Play the IMS and H.E.A.R.T platform walkthrough"
                data-testid="hart-video-play">
                <img
                  src="https://img.youtube.com/vi/Ng1roAYbGJc/maxresdefault.jpg"
                  alt="IMS and H.E.A.R.T platform walkthrough"
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover opacity-60
                    group-hover:opacity-75 transition-opacity duration-300"
                />
                <span className="absolute inset-0 bg-gradient-to-t from-[#030712] via-transparent to-transparent" />
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="inline-flex items-center gap-3 rounded-md border border-white/20
                    bg-[#030712]/80 px-6 py-3.5 backdrop-blur-sm transition-colors duration-200
                    group-hover:border-sky-400/60 group-hover:bg-[#030712]/90">
                    <Play className="w-4 h-4 text-sky-400" fill="currentColor" />
                    <span className="text-xs font-bold uppercase tracking-[0.18em] text-white">
                      Play the walkthrough
                    </span>
                  </span>
                </span>
              </button>
            )}
          </div>
          <p className="mt-3 text-[11px] text-slate-500">
            Hosted on YouTube. Loads only when you press play, so it does not slow this page down.
          </p>
        </div>
      </section>

      {/* ============== WHERE THE MONEY LEAKS — light band ============== */}
      <section id="problem" className="py-20 md:py-28 bg-slate-50 border-b border-slate-200"
        data-testid="hart-problem">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-3xl mb-12">
            <PillLight icon={Search}>Where the money leaks</PillLight>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0a1c3c] tracking-tight leading-[1.1] mb-5">
              Everyone owns the spreadsheet.{" "}
              <span className="text-[#0284C7]">Nobody owns the chase.</span>
            </h2>
            <p className="text-base md:text-lg text-slate-600 leading-relaxed">
              Rebate agreements concentrate in your highest-value spend — and that is exactly where
              the process breaks. The contract lives in a PDF. The tiers live in an inbox. Recovery
              is nobody&apos;s job title.
            </p>
          </div>
          <LeakPillars />
          <div className="mt-8"><NearMiss /></div>
        </div>
      </section>

      {/* ============== WHAT H.E.A.R.T DOES — white band ============== */}
      <section id="capabilities" className="py-20 md:py-24 bg-white" data-testid="hart-capabilities">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-3xl mb-10">
            <PillLight icon={CheckCircle2}>What H.E.A.R.T does</PillLight>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0a1c3c] tracking-tight leading-[1.1] mb-4">
              Every rebate dollar — <span className="text-[#0056A8]">tracked, alerted, recovered.</span>
            </h2>
            <p className="text-base md:text-lg text-slate-600">
              Not a report. A working system, where every capability feeds the next dollar recovered.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {CAPABILITIES.map((c, i) => (
              <motion.div key={c.t}
                initial={reduce ? false : { opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.05 }}
                transition={{ delay: 0.06 * i, duration: 0.45 }}
                className="group rounded-xl border border-slate-200 bg-white p-6 shadow-sm
                  hover:shadow-xl hover:border-[#0056A8]/40 transition-all"
                data-testid={`capability-${i}`}>
                <CheckCircle2 className="w-5 h-5 text-[#0056A8] mb-3" />
                <h3 className="text-[17px] font-bold text-[#0a1c3c] mb-1.5">{c.t}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{c.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== FIVE ALERTS — white ===================== */}
      <section id="alerts" className="py-20 md:py-24 bg-slate-50 border-y border-slate-200"
        data-testid="hart-alerts">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-3xl mb-10">
            <PillLight>Where the intelligence runs</PillLight>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0a1c3c] tracking-tight leading-[1.1] mb-4">
              Five alerts. Each one arrives with money, a clock, and a name.
            </h2>
            <p className="text-base md:text-lg text-slate-600">
              Triage is automatic — the queue is ranked by dollars at stake, not by the date the
              email landed.
            </p>
          </div>
          <AlertWorkbench />
        </div>
      </section>

      {/* ================= HOW IT WORKS — white ================= */}
      <section id="how" className="py-20 md:py-24 bg-white" data-testid="hart-how">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-3xl mb-12">
            <PillLight>How it works · ERP-integrated</PillLight>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0a1c3c] tracking-tight leading-[1.1] mb-4">
              Follow one dollar — contract load to recovered payment.
            </h2>
            <p className="text-base md:text-lg text-slate-600">
              Nothing expires unseen. Nothing is underpaid quietly. Every open dollar has an owner
              and a deadline.
            </p>
          </div>
          <FollowOneDollar />
        </div>
      </section>

      {/* ================ ARCHITECTURE — navy band ================ */}
      <section id="architecture" className="py-20 md:py-28 bg-slate-50 border-y border-slate-200"
        data-testid="hart-architecture">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <PillLight icon={ShieldCheck}>Secure by design</PillLight>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#0a1c3c] tracking-tight leading-[1.1] mb-4">
              Cloud SaaS on AWS. Runs off the ERP you already own.
            </h2>
            <p className="text-base text-slate-600 leading-relaxed mb-6">
              Purchases, invoices and remittances stream in from your ERP. H.E.A.R.T matches, monitors
              and alerts — no servers to buy, nothing for IT to host.
            </p>
            <div className="flex flex-wrap gap-2 mb-7" data-testid="erp-row">
              {ERPS.map((e) => (
                <span key={e} className="inline-flex items-center gap-2 rounded-md border border-slate-200
                  bg-white px-3.5 py-2 text-[13px] font-bold tracking-tight text-slate-700">
                  <Database className="w-3.5 h-3.5 text-sky-400" /> {e}
                </span>
              ))}
            </div>
            <div className="rounded-lg border border-[#0284C7]/25 bg-[#0284C7]/[0.06] p-5"
              data-testid="no-epic-callout">
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#0284C7] mb-2">
                Is H.E.A.R.T integrated with Epic?
              </p>
              <p className="text-sm text-slate-700 leading-relaxed">
                <strong className="text-[#0a1c3c]">No — and it doesn&apos;t need to be.</strong> H.E.A.R.T is
                fed from your ERP, where purchases, invoices and remittances live. With IMS running,
                the bill-only spend entering that ERP is already contract-validated, so H.E.A.R.T
                inherits clean data without touching Epic.
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {SECURITY.map((s, i) => (
              <motion.div key={s.t}
                initial={reduce ? false : { opacity: 0, scale: 0.96 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, amount: 0.05 }}
                transition={{ delay: 0.07 * i, duration: 0.4 }}
                className="rounded-lg border border-slate-200 bg-white p-5
                  hover:border-[#0284C7]/40 transition-colors">
                <s.icon className="w-5 h-5 text-sky-400 mb-3" />
                <h3 className="text-[15px] font-bold text-[#0a1c3c] mb-1">{s.t}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
            <div className="sm:col-span-2 rounded-lg border border-slate-200 bg-white p-5
              flex items-center gap-3">
              <Server className="w-5 h-5 text-slate-400 shrink-0" />
              <p className="text-[13px] text-slate-600">
                Multi-tenant redundancy, audit logs on every record, and a deployment footprint of
                exactly zero on your estate.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== SIX PRODUCTS — white ==================== */}
      <section id="suite" className="py-20 md:py-24 bg-white" data-testid="hart-suite">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-3xl mb-10">
            <PillLight icon={Boxes}>Six products. Built for healthcare.</PillLight>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0a1c3c] tracking-tight leading-[1.1] mb-4">
              Two of the six attack the largest money leaks in hospital supply chain.
            </h2>
            <p className="text-base md:text-lg text-slate-600">
              A healthcare software company — 25+ years inside hospital IT, 12 years building this
              product line, shaped screen by screen with supply chain leadership.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {PRODUCTS.map((p, i) => (
              <motion.a key={p.id} href={p.id === "ims" ? "#ims" : "#demo"}
                initial={reduce ? false : { opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.05 }}
                transition={{ delay: 0.05 * i, duration: 0.4 }}
                className="group rounded-xl border border-slate-200 bg-white p-6 shadow-sm
                  hover:shadow-xl hover:border-[#0056A8]/40 transition-all"
                data-testid={`product-${p.id}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <p.icon className="w-5 h-5 text-[#0056A8]" />
                  <span className="text-[9px] font-black uppercase tracking-[0.16em] text-slate-500
                    border border-slate-200 rounded-full px-2 py-0.5">{p.badge}</span>
                </div>
                <h3 className="text-[17px] font-bold text-[#0a1c3c] mb-1.5">{p.t}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{p.d}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-[11px] font-bold text-[#0056A8]
                  opacity-0 group-hover:opacity-100 transition-opacity">
                  Explore <ArrowRight className="w-3 h-3" />
                </span>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* ============== IMS BILL-ONLY IMPLANTS — navy band ============== */}
      <section id="ims" className="py-20 md:py-28 bg-[#0B132B]"
        data-testid="ims-module">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Pill icon={Boxes}>Module · the second money leak</Pill>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-white tracking-tight leading-[1.1] mb-4">
              IMS Bill-Only Implants &amp; tray management
            </h2>
            <p className="text-base text-slate-300 leading-relaxed mb-5">
              Bill-only implant spend is the other place the money goes. IMS manages the procedure
              end to end: the case, the implants used, the tray that came in, and the contract price
              each line must honour. Suppliers submit in the portal, so what reaches your ERP is
              already contract-validated — which is exactly the clean data H.E.A.R.T needs.
            </p>
            <ul className="space-y-2.5 mb-7">
              {[
                "Contract compliance enforced at the line, before the invoice is accepted",
                "Suppliers submit implant usage and pricing in the portal, not by email",
                "Tray tracking and instrument sets tied to the case and the location",
                "Case-level visibility into implant usage, cost and status",
                "Audit-ready history on every implant, every case, every adjustment",
              ].map((l) => (
                <li key={l} className="flex items-start gap-2.5 text-sm text-slate-200">
                  <CheckCircle2 className="w-4 h-4 text-sky-400 mt-0.5 shrink-0" />{l}
                </li>
              ))}
            </ul>
            <a href="#demo"
              className="inline-flex items-center gap-2 rounded-lg bg-[#0056A8] hover:bg-[#003366]
                px-6 py-3.5 text-xs font-bold uppercase tracking-wider text-white transition-colors"
              data-testid="ims-demo-cta">
              See IMS in the demo <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>

          <div>
            <div className="rounded-lg border border-white/10 bg-white/[0.03] p-6">
              <p className="text-[10px] font-black uppercase tracking-[0.24em] text-sky-400 mb-4">
                Why the two run together
              </p>
              <p className="text-sm text-slate-300 leading-relaxed mb-5">
                IMS validates bill-only spend at the line before it is accepted. That validated
                spend is what flows into your ERP &mdash; and H.E.A.R.T reads the ERP. So the
                rebate math is calculated on data that was already proven correct, rather than on
                whatever the invoice happened to say.
              </p>
              <a href="#walkthrough"
                className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em]
                  text-sky-400 hover:text-sky-300 transition-colors"
                data-testid="ims-walkthrough-link">
                <Play className="w-3.5 h-3.5" /> See it in the walkthrough
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ====================== ROI MODEL — slate band ====================== */}
      <section id="roi" className="py-20 md:py-24 bg-slate-50 border-y border-slate-200"
        data-testid="hart-roi">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-3xl mb-10">
            <PillLight icon={Calculator}>For the CFO · 60 seconds</PillLight>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0a1c3c] tracking-tight leading-[1.1] mb-4">
              Move four numbers. <span className="text-[#0056A8]">See what recovery is worth.</span>
            </h2>
            <p className="text-base md:text-lg text-slate-600">
              Your spend, your footprint, what you capture today. The model splits the gap into
              near-misses, supplier underpayments, expiries and the analyst hours you get back —
              then prints it on one page you can hand to finance.
            </p>
          </div>
          <HartRoiCalculator onResult={setRoi} />
        </div>
      </section>

      {/* ========================= DEMO — white ========================= */}
      <section id="demo" className="py-20 md:py-24 bg-white" data-testid="hart-demo">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-[0.9fr_1.1fr] gap-12 items-center">
          <div>
            <PillLight icon={CircleDollarSign}>Talk to operations</PillLight>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0a1c3c] tracking-tight leading-[1.1] mb-4">
              Find the near-miss before the quarter closes.
            </h2>
            <p className="text-base md:text-lg text-slate-600 leading-relaxed mb-6">
              We&apos;ll walk your agreements, show where the tiers sit today and what the calendar
              is costing. If the dollars aren&apos;t there, we&apos;ll tell you that too.
            </p>
            <div className="space-y-3">
              {[
                "Live sandbox — your contract structures, not a canned deck",
                "A first pass at the rebate dollars currently unclaimed",
                "No ERP connection needed to see it run",
              ].map((l) => (
                <p key={l} className="flex items-start gap-2.5 text-sm text-slate-700">
                  <CheckCircle2 className="w-4 h-4 text-[#0056A8] mt-0.5 shrink-0" />{l}
                </p>
              ))}
            </div>
            <p className="mt-7 text-[11px] text-slate-500">
              Every dollar figure on this page is illustrative, drawn from typical health-system
              contract structures.
            </p>
          </div>
          <HartDemoForm roiSnapshot={roi} />
        </div>
      </section>

      {/* ============================= FOOTER ============================= */}
      <footer className="relative bg-[#0a0f1a] py-10 text-slate-300">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-wrap items-center justify-between gap-4">
          <div data-testid="heart-wordmark-footer">
            <HeartLogo size={44} tone="dark" idSuffix="footer" />
            <p className="text-[11px] text-slate-500 mt-3">
              Sterile Mate LLC × TechInquest · 396 W Greens Road, Suite 1139, Houston TX 77067 ·
              (281) 456-3458
            </p>
          </div>
          <Link to="/solutions"
            className="inline-flex items-center gap-2 text-[12px] font-bold text-sky-400 hover:text-white"
            data-testid="back-to-solutions">
            All solutions <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </footer>
    </div>
  );
}
